import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zxcvbn } from '@zxcvbn-ts/core';
import Footer from '@/components/Footer';
import PageWrapper from '@/components/PageWrapper';
import ErrorLine from '@/components/ErrorLine';
import { generatedApiClient } from '@/services/generatedApiClient';

const primary = 'var(--primary-base)';
const greyBg = 'var(--surface-surface_2)';
const greyText = 'var(--text-low_em)';
const highText = 'var(--text-high_em)';
const errorCol = 'var(--error-color)';

const RegisterActivatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = () => {
    if (!token) return 'Activation token is missing';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (zxcvbn(password).score < 2) return 'Choose a stronger password';
    if (password !== passwordConfirm) return "Passwords don't match";
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await generatedApiClient.activateRegistration({
        activation_token: token,
        password,
        password_confirm: passwordConfirm,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      navigate('/text-chat', { replace: true });
    } catch (e: unknown) {
      setError((e as Error)?.message || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface-surface_0)',
        }}
      >
        <PageWrapper>
          <form
            onSubmit={handleSubmit}
            style={{
              width: 480,
              padding: '48px 32px',
              borderRadius: 24,
              background: 'var(--surface-surface_0)',
              border: '1px solid var(--outline-base_em, #F4F4F6)',
              boxShadow:
                '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), 0px 24px 24px -12px var(--elevationshadow)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 24,
            }}
          >
            <h1
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: 64,
                lineHeight: '48px',
                color: primary,
                margin: 0,
              }}
            >
              swarmind
            </h1>

            <h2 style={{ margin: 0 }}>Set password</h2>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: greyText }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  background: greyBg,
                  color: highText,
                  WebkitTextFillColor: highText,
                  border: `1px solid ${error ? errorCol : 'transparent'}`,
                  padding: '0 12px',
                  fontSize: 15,
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: greyText }}>Confirm password</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={e => setPasswordConfirm(e.target.value)}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  background: greyBg,
                  color: highText,
                  WebkitTextFillColor: highText,
                  border: `1px solid ${error ? errorCol : 'transparent'}`,
                  padding: '0 12px',
                  fontSize: 15,
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            {error && <ErrorLine>{error}</ErrorLine>}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 12,
                background: loading ? greyBg : primary,
                color: loading ? greyText : '#fff',
                border: 'none',
                fontWeight: 600,
                fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Saving...' : 'Activate account'}
            </button>
          </form>
        </PageWrapper>
      </main>
      <Footer />
    </div>
  );
};

export default RegisterActivatePage;
